import React from 'react';
import {
  Box,
  Typography,
  Button,
  Divider,
  BoxProps,
  SxProps,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@mui/lab';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';

import { IGroupedSubTopic, ISubTopic } from '../../../../store/SubTopicSlice';
import { ChipNew } from '../../Subject/components/SubTopicList';
import { IQuestion } from '../../../../store/QuestionSlice';

const topicBoxProps: BoxProps = {
  borderRadius: '10px',
  p: '10px',
};

const navButtonBoxProps: BoxProps = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  minWidth: 'fit-content',
  mt: 1,
};

const timeLineStyles: SxProps = {
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#E2E8F0',
    borderRadius: '5px',
    maxWidth: '260px',
    maxHeight: '35px',
  },
};

const chipStyles: SxProps = {
  position: 'relative',
  marginLeft: '7px',
  borderRadius: '4px',
  fontSize: '10px',
  height: '18px',
  padding: '0 6px',
  color: 'transparent',
  backgroundImage: 'linear-gradient(90deg, #DB86F8 0%, #006FD6 100%)',
  backgroundClip: 'text',
  textFillColor: 'transparent',
  '& .MuiChip-label': {
    padding: '0',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: '4px',
    padding: '1px',
    background: 'linear-gradient(90deg, #DB86F8 0%, #006FD6 100%)',
    WebkitMask:
      'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
  },
};

interface SubTopicSectionProps {
  subTopicData: ISubTopic;
  subjectColor: string;
  questionList: IQuestion[];
  handleButtonClick: () => void;
  handleNextSubTopic: () => void;
  handlePreviousSubTopic: () => void;
  selectedSubTopicIndex: number;
  subTopics: Array<IGroupedSubTopic>;
}

const SubTopicSection: React.FC<SubTopicSectionProps> = ({
  subTopicData,
  subjectColor,
  questionList,
  handleButtonClick,
  handleNextSubTopic,
  handlePreviousSubTopic,
  selectedSubTopicIndex,
  subTopics,
}) => {
  const theme = useTheme();
  const belowLg = useMediaQuery(theme.breakpoints.down('lg'));

  const handleChange = (id: string) => {
    const section = document.getElementById(id);

    if (section) {
      const offset = 80;
      const elementPosition =
        section.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });

      const accordionHeader = section.querySelector(
        '.MuiAccordionSummary-root'
      ) as HTMLElement;
      if (accordionHeader && !section.querySelector('.Mui-expanded')) {
        accordionHeader.click();
      }
    }
  };

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderRadius: '20px',
        p: 2,
        marginRight: !belowLg ? '15px' : null,
        marginBottom: belowLg ? '15px' : null,
        minHeight: belowLg ? 'unset' : '70vh',
      }}
    >
      <Box {...topicBoxProps} sx={{ backgroundColor: `${subjectColor}0A` }}>
        <Typography
          fontSize="16px"
          letterSpacing="0.5px"
          fontWeight={600}
          align="center"
          color={subjectColor}
        >
          {subTopicData.name}
        </Typography>
      </Box>
      <Box {...navButtonBoxProps} sx={{ mb: !belowLg ? 2 : null }}>
        <Button
          onClick={handlePreviousSubTopic}
          disabled={selectedSubTopicIndex <= 0}
          sx={{ color: subjectColor, fontSize: '16px' }}
        >
          <KeyboardArrowLeft />
          Previous
        </Button>
        <Button
          onClick={handleNextSubTopic}
          disabled={selectedSubTopicIndex >= subTopics.length - 1}
          sx={{ color: subjectColor, fontSize: '16px' }}
        >
          Next
          <KeyboardArrowRight />
        </Button>
      </Box>
      {!belowLg && (
        <>
          <Divider sx={{ border: '0.5px dashed #CBD5E1', mb: 2, mt: 2 }} />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'left',
              mb: '-6px',
            }}
          >
            <Timeline
              sx={{
                ml: '-230px',
                '& .MuiTimelineContent-root': {
                  textAlign: 'left',
                },
              }}
            >
              {subTopicData.resources?.length ? (
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot variant="outlined" />
                    {subTopicData.contents?.length ||
                    subTopicData.videoLinks?.length ||
                    questionList.length ||
                    (subTopicData?.quizCount ?? 0) > 0 ? (
                      <TimelineConnector />
                    ) : null}
                  </TimelineSeparator>
                  <TimelineContent
                    onClick={() => handleChange('resources')}
                    sx={timeLineStyles}
                  >
                    <Typography fontSize="16px">Resources</Typography>
                  </TimelineContent>
                </TimelineItem>
              ) : null}
              {subTopicData.contents && subTopicData.contents.length ? (
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot variant="outlined" />
                    {subTopicData.videoLinks?.length ||
                    questionList.length ||
                    (subTopicData?.quizCount ?? 0) > 0 ? (
                      <TimelineConnector />
                    ) : null}
                  </TimelineSeparator>
                  <TimelineContent
                    onClick={() => handleChange('exam-tips')}
                    sx={timeLineStyles}
                  >
                    <Typography fontSize="16px">Exam Tips</Typography>
                  </TimelineContent>
                </TimelineItem>
              ) : null}
              {subTopicData.videoLinks && subTopicData.videoLinks.length ? (
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot variant="outlined" />
                    {questionList.length ||
                    (subTopicData?.quizCount ?? 0) > 0 ? (
                      <TimelineConnector />
                    ) : null}
                  </TimelineSeparator>
                  <TimelineContent
                    onClick={() => handleChange('videos-library')}
                    sx={timeLineStyles}
                  >
                    <Typography fontSize="16px">Video</Typography>
                  </TimelineContent>
                </TimelineItem>
              ) : null}
              {subTopicData.resources?.length && questionList.length ? (
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot variant="outlined" />
                    {subTopicData.quizCount && subTopicData.quizCount > 0 ? (
                      <TimelineConnector />
                    ) : null}
                  </TimelineSeparator>
                  <TimelineContent
                    onClick={() => handleChange('practice-questions')}
                    sx={timeLineStyles}
                  >
                    <Box display="flex" flexDirection="row">
                      <Typography fontSize="16px">
                        Practice Qusetions
                      </Typography>
                      <ChipNew />
                    </Box>
                  </TimelineContent>
                </TimelineItem>
              ) : null}
              {subTopicData.quizCount && subTopicData.quizCount > 0 ? (
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot variant="outlined" />
                  </TimelineSeparator>
                  <TimelineContent
                    onClick={handleButtonClick}
                    sx={timeLineStyles}
                  >
                    <Box display="flex" flexDirection="row" alignItems="center">
                      <Typography fontSize="16px">Exam Quiz</Typography>
                      <Chip
                        size="small"
                        label="AI Marking Scheme"
                        sx={chipStyles}
                      />
                    </Box>
                  </TimelineContent>
                </TimelineItem>
              ) : null}
            </Timeline>
          </Box>
        </>
      )}
    </Box>
  );
};

export default SubTopicSection;
